import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet } from '@/lib/api';
import WeeklyGrowthCard from '@/components/WeeklyGrowthCard';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Flame, Brain, Target, MessageCircle, Zap, Star, Trophy, Award,
         TrendingUp, Calendar, BookOpen, Crown, AlertTriangle, CheckCircle2, Layers, Share2 } from 'lucide-react';
import { KnowledgeTree, StreakBadge, ShareableStreakCard } from '@/components/MicroWins';
import { ParentContactsNudge } from '@/components/ParentContactsCard';

const BADGE_ICONS = {
  target: Target, swords: Brain, brain: Brain, hundred: Award, sparkles: Zap,
  flame: Flame, fire: Flame, mountain: TrendingUp, lightbulb: MessageCircle,
  book: BookOpen, zap: Zap, star: Star, trophy: Trophy, gem: Award, crown: Crown,
};

const DONUT_COLORS = ['#6366F1','#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#14B8A6'];

function ScoreTrendChart({ data }) {
  if (!data || data.length === 0) return <p className="text-sm text-slate-400 font-body text-center py-8">Take quizzes to see your score trend</p>;
  const w = 500, h = 200, pad = 40;
  const scores = data.map(d => d.score);
  const avgs = data.map(d => d.avg);
  const maxY = 100, minY = 0;
  const toX = (i) => pad + (i / Math.max(data.length - 1, 1)) * (w - pad * 2);
  const toY = (v) => h - pad - ((v - minY) / (maxY - minY)) * (h - pad * 2);
  const scorePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(d.score)}`).join(' ');
  const avgPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(d.avg)}`).join(' ');
  const areaPath = scorePath + ` L${toX(data.length - 1)},${toY(0)} L${toX(0)},${toY(0)} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="score-trend-chart">
      {[0, 25, 50, 75, 100].map(v => (
        <g key={v}>
          <line x1={pad} y1={toY(v)} x2={w - pad} y2={toY(v)} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="4,4" />
          <text x={pad - 8} y={toY(v) + 4} textAnchor="end" fill="#94A3B8" fontSize="10" fontFamily="DM Sans">{v}</text>
        </g>
      ))}
      <path d={areaPath} fill="rgba(99,102,241,0.1)" />
      <path d={scorePath} fill="none" stroke="#6366F1" strokeWidth="2.5" strokeLinecap="round" />
      <path d={avgPath} fill="none" stroke="#A855F7" strokeWidth="1.5" strokeDasharray="5,5" />
      {data.map((d, i) => <circle key={i} cx={toX(i)} cy={toY(d.score)} r="4" fill="#6366F1" />)}
      {data.length > 0 && <text x={toX(0)} y={h - 8} textAnchor="middle" fill="#94A3B8" fontSize="9" fontFamily="DM Sans">{data[0].date}</text>}
      {data.length > 1 && <text x={toX(data.length - 1)} y={h - 8} textAnchor="middle" fill="#94A3B8" fontSize="9" fontFamily="DM Sans">{data[data.length - 1].date}</text>}
    </svg>
  );
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatChartDate(dateStr) {
  const parts = dateStr.split('-');
  if (parts.length >= 3) return `${MONTHS[parseInt(parts[1], 10) - 1]}-${parts[2]}`;
  return dateStr;
}

function ActivityChart({ data }) {
  // Use dummy data if all counts are 0 to demonstrate the chart
  let chartData = data;
  const hasRealData = data && data.some(d => d.count > 0);
  if (!hasRealData) {
    const today = new Date();
    chartData = Array.from({ length: 14 }, (_, i) => {
      const d = new Date(today); d.setDate(d.getDate() - (13 - i));
      const dateStr = d.toISOString().slice(0, 10);
      const dummyCounts = [1,0,2,1,3,0,1,2,0,3,1,2,0,4];
      return { date: dateStr, count: dummyCounts[i] };
    });
  }
  if (!chartData || chartData.length === 0) return null;

  const maxCount = Math.max(...chartData.map(d => d.count), 1);
  const w = 600, h = 200, padL = 35, padR = 15, padT = 10, padB = 35;
  const chartW = w - padL - padR;
  const chartH = h - padT - padB;
  const barW = chartW / chartData.length * 0.6;
  const gap = chartW / chartData.length;
  const gridLines = [];
  for (let v = 0; v <= maxCount + 1; v++) gridLines.push(v);

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="activity-chart">
        {gridLines.map(v => {
          const y = padT + chartH - (v / (maxCount + 1)) * chartH;
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="4,4" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fill="#94A3B8" fontSize="10" fontFamily="DM Sans">{v}</text>
            </g>
          );
        })}
        {chartData.map((d, i) => {
          const barH = Math.max((d.count / (maxCount + 1)) * chartH, d.count > 0 ? 4 : 0);
          const x = padL + i * gap + (gap - barW) / 2;
          const y = padT + chartH - barH;
          const showLabel = i === 0 || i === Math.floor(chartData.length / 2) || i === chartData.length - 1;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH} rx="4" fill="#6366F1" opacity="0.85" />
              {showLabel && (
                <text x={x + barW / 2} y={h - 8} textAnchor="middle" fill="#64748B" fontSize="10" fontFamily="DM Sans" fontWeight="500">
                  {formatChartDate(d.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {!hasRealData && <p className="text-xs text-slate-400 font-body text-center mt-1 italic">Sample data shown — your real activity will appear as you study</p>}
    </div>
  );
}

function DonutChart({ data }) {
  if (!data || data.length === 0) return <p className="text-sm text-slate-400 font-body text-center py-8">Take quizzes in different subjects to see breakdown</p>;
  const total = data.reduce((s, d) => s + d.count, 0);
  const cx = 120, cy = 120, r = 80, innerR = 50;
  let cumAngle = -Math.PI / 2;

  return (
    <div className="flex items-center justify-center gap-8 flex-wrap" data-testid="subject-donut">
      <svg viewBox="0 0 240 240" className="w-48 h-48">
        {data.map((d, i) => {
          const angle = (d.count / total) * Math.PI * 2;
          const x1 = cx + r * Math.cos(cumAngle);
          const y1 = cy + r * Math.sin(cumAngle);
          const x2 = cx + r * Math.cos(cumAngle + angle);
          const y2 = cy + r * Math.sin(cumAngle + angle);
          const ix1 = cx + innerR * Math.cos(cumAngle + angle);
          const iy1 = cy + innerR * Math.sin(cumAngle + angle);
          const ix2 = cx + innerR * Math.cos(cumAngle);
          const iy2 = cy + innerR * Math.sin(cumAngle);
          const large = angle > Math.PI ? 1 : 0;
          const path = `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${innerR} ${innerR} 0 ${large} 0 ${ix2} ${iy2} Z`;
          cumAngle += angle;
          return <path key={i} d={path} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />;
        })}
      </svg>
      <div className="space-y-2">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="text-sm font-body text-slate-700">{d.subject} ({d.count})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SUBJECT_COLORS = {
  'Mathematics': { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', accent: '#3B82F6' },
  'Physics': { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', accent: '#6366F1' },
  'Chemistry': { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', accent: '#10B981' },
  'Biology': { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', accent: '#22C55E' },
  'English': { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', accent: '#F59E0B' },
  'Hindi': { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', accent: '#F97316' },
  'Social Science': { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', accent: '#8B5CF6' },
  'Computer Science': { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-200', accent: '#06B6D4' },
  'Science': { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200', accent: '#14B8A6' },
};

const getSubjectColor = (subj) => SUBJECT_COLORS[subj] || { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', accent: '#64748B' };

function SubjectCard({ subject }) {
  const colors = getSubjectColor(subject.subject);
  const hasQuizzes = subject.quizzes > 0;
  const hasImprovement = subject.areas_of_improvement?.length > 0;

  return (
    <Card className={`rounded-2xl border ${colors.border} overflow-hidden`} data-testid={`subject-card-${subject.subject.toLowerCase().replace(/\s/g, '-')}`}>
      {/* Header */}
      <div className={`${colors.bg} px-5 py-4 flex items-center justify-between`}>
        <div>
          <h4 className={`font-heading text-base font-bold ${colors.text}`}>{subject.subject}</h4>
          <p className="text-xs text-slate-500 font-body mt-0.5">
            {subject.total_topics} topics in syllabus
          </p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
          <span className={`font-heading text-lg font-bold ${colors.text}`}>{subject.coverage_pct}%</span>
        </div>
      </div>

      {/* Stats Row */}
      <div className="px-5 py-3 grid grid-cols-3 gap-3 border-b border-slate-100">
        <div className="text-center">
          <p className="font-heading text-lg font-bold text-slate-800">{subject.chat_sessions}</p>
          <p className="text-[10px] text-slate-400 font-body">Discussions</p>
        </div>
        <div className="text-center">
          <p className="font-heading text-lg font-bold text-slate-800">{subject.quizzes}</p>
          <p className="text-[10px] text-slate-400 font-body">Quizzes</p>
        </div>
        <div className="text-center">
          <p className="font-heading text-lg font-bold text-slate-800">{subject.flashcards}</p>
          <p className="text-[10px] text-slate-400 font-body">Flashcards</p>
        </div>
      </div>

      {/* Coverage & Score */}
      <div className="px-5 py-3 space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-body text-slate-500">Topic Coverage</span>
            <span className="text-xs font-heading font-semibold text-slate-700">{subject.topics_covered}/{subject.total_topics}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${subject.coverage_pct}%`, backgroundColor: colors.accent }} />
          </div>
        </div>
        {hasQuizzes && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-body text-slate-500">Avg Quiz Score</span>
              <span className={`text-xs font-heading font-semibold ${subject.avg_quiz_score >= 70 ? 'text-emerald-600' : subject.avg_quiz_score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{subject.avg_quiz_score}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${subject.avg_quiz_score >= 70 ? 'bg-emerald-500' : subject.avg_quiz_score >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${subject.avg_quiz_score}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Areas of Improvement */}
      {hasImprovement && (
        <div className="px-5 py-3 border-t border-slate-100">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-heading font-semibold text-slate-700">Areas to Improve</span>
          </div>
          <div className="space-y-1.5">
            {subject.areas_of_improvement.slice(0, 4).map((area, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid={`improvement-${i}`}>
                {area.type === 'low_score' ? (
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                ) : (
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                )}
                <span className="font-body text-slate-600 truncate flex-1">{area.topic}</span>
                <span className={`font-body flex-shrink-0 ${area.type === 'low_score' ? 'text-red-500' : 'text-slate-400'}`}>
                  {area.reason}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function SubjectsTab({ subjectAnalytics }) {
  if (!subjectAnalytics || subjectAnalytics.length === 0) {
    return (
      <Card className="p-8 rounded-2xl border-slate-100 text-center">
        <Layers className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h3 className="font-heading text-base font-semibold text-slate-600 mb-1">No Subject Activity Yet</h3>
        <p className="text-sm text-slate-400 font-body">Start chatting, take quizzes, or create flashcards to see your subject-wise progress here.</p>
      </Card>
    );
  }

  // Overall summary
  const totalChats = subjectAnalytics.reduce((s, d) => s + d.chat_sessions, 0);
  const totalQuizzes = subjectAnalytics.reduce((s, d) => s + d.quizzes, 0);
  const totalFlashcards = subjectAnalytics.reduce((s, d) => s + d.flashcards, 0);
  const overallCoverage = subjectAnalytics.length > 0
    ? Math.round(subjectAnalytics.reduce((s, d) => s + d.coverage_pct, 0) / subjectAnalytics.length)
    : 0;

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <Card className="p-4 rounded-2xl border-slate-100 bg-gradient-to-r from-slate-50 to-blue-50/30" data-testid="subjects-summary">
        <div className="grid grid-cols-4 gap-3 text-center">
          <div>
            <p className="font-heading text-xl font-bold text-slate-800">{subjectAnalytics.length}</p>
            <p className="text-[10px] text-slate-400 font-body">Subjects</p>
          </div>
          <div>
            <p className="font-heading text-xl font-bold text-slate-800">{totalChats}</p>
            <p className="text-[10px] text-slate-400 font-body">Discussions</p>
          </div>
          <div>
            <p className="font-heading text-xl font-bold text-slate-800">{totalQuizzes}</p>
            <p className="text-[10px] text-slate-400 font-body">Quizzes</p>
          </div>
          <div>
            <p className="font-heading text-xl font-bold text-emerald-600">{overallCoverage}%</p>
            <p className="text-[10px] text-slate-400 font-body">Avg Coverage</p>
          </div>
        </div>
      </Card>

      {/* Subject Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {subjectAnalytics.map(subj => (
          <SubjectCard key={subj.subject} subject={subj} />
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [gam, setGam] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [subjectAnalytics, setSubjectAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareStreak, setShowShareStreak] = useState(false);

  useEffect(() => {
    Promise.all([
      apiGet('/gamification').catch(() => null),
      apiGet('/leaderboard').catch(() => null),
      apiGet('/subject-analytics').catch(() => null),
    ]).then(([g, l, sa]) => { setGam(g); setLeaderboard(l); setSubjectAnalytics(sa); setLoading(false); });
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const g = gam || {};

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-8" data-testid="dashboard-page">
      {/* Parent-contact nudge (only renders if no parent number is on file) */}
      {(user?.role === 'student' || !user?.role) && <ParentContactsNudge />}
      {(user?.role === 'student' || !user?.role) && <div className="mb-4 sm:mb-6"><WeeklyGrowthCard /></div>}

      {/* Profile Card */}
      <Card className="p-4 sm:p-5 md:p-6 rounded-2xl border-slate-100 mb-4 sm:mb-6 bg-gradient-to-r from-slate-50 to-blue-50/30 animate-slide-up" data-testid="profile-header">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="relative">
            <Avatar className="w-12 h-12 sm:w-16 sm:h-16 border-2 border-blue-200">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-blue-100 text-blue-600 font-heading text-base sm:text-xl">{user?.name?.[0] || 'U'}</AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] sm:text-xs font-heading font-bold border-2 border-white">{g.level || 1}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-base sm:text-xl font-bold text-slate-900 truncate">{user?.name?.toUpperCase()}</h2>
              <Badge className="bg-blue-50 text-blue-600 text-[10px] sm:text-xs font-body">Learner</Badge>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <Zap className="w-4 h-4 text-amber-500" /><span className="font-heading text-sm font-semibold text-slate-700">{g.xp || 0} Veda Coins</span>
              <span className="text-xs text-slate-400 font-body">Level {g.level || 1}</span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Progress value={((g.xp_in_level || 0) / (g.xp_for_next || 100)) * 100} className="h-2.5 flex-1" />
              <span className="text-xs text-slate-400 font-body whitespace-nowrap">{g.xp_in_level || 0}/{g.xp_for_next || 100} Veda Coins</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 stagger-children">
        {[
          { icon: Flame, label: 'Day Streak', value: g.streak || 0, color: 'text-orange-500', extra: (g.streak || 0) > 0 ? '\uD83D\uDD25' : '' },
          { icon: Brain, label: 'Quizzes', value: g.quizzes_completed || 0, color: 'text-blue-500' },
          { icon: Target, label: 'Avg Score', value: `${g.avg_score || 0}%`, color: 'text-emerald-500' },
          { icon: MessageCircle, label: 'Questions', value: g.questions_asked || 0, color: 'text-purple-500' },
        ].map(stat => (
          <Card key={stat.label} className="p-4 rounded-2xl border-slate-100" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-start gap-3">
              <stat.icon className={`w-5 h-5 ${stat.color} mt-0.5`} />
              <div><p className="font-heading text-2xl font-bold text-slate-900">{stat.value}</p><p className="text-xs text-slate-500 font-body">{stat.label}</p>{stat.extra && <span className="text-sm">{stat.extra}</span>}</div>
            </div>
          </Card>
        ))}
      </div>

      {/* Knowledge Tree + Streak */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card className="p-5 rounded-2xl border-slate-100 flex flex-col items-center" data-testid="knowledge-tree-card">
          <h3 className="font-heading text-sm font-semibold text-slate-700 mb-2">Knowledge Tree</h3>
          <KnowledgeTree topicsCompleted={g.topics_completed || 0} totalTopics={g.total_topics || 0} />
        </Card>
        <Card className="p-5 rounded-2xl border-slate-100" data-testid="streak-card">
          <h3 className="font-heading text-sm font-semibold text-slate-700 mb-3">Study Streak</h3>
          <StreakBadge days={g.streak || 0} />
          <button onClick={() => setShowShareStreak(true)} data-testid="share-streak-btn"
            className="mt-3 flex items-center gap-1.5 text-xs text-blue-500 font-heading hover:underline">
            <Share2 className="w-3.5 h-3.5" /> Share Your Streak
          </button>
        </Card>
      </div>

      {/* Daily Challenges */}
      {g.daily_challenges && (
        <Card className="p-5 rounded-2xl border-slate-100 mb-6 animate-slide-up" data-testid="daily-challenges">
          <div className="flex items-center gap-2 mb-4"><Zap className="w-5 h-5 text-amber-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Daily Challenges</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {g.daily_challenges.map(ch => (
              <Card key={ch.id} className="p-4 rounded-xl border-slate-100" data-testid={`challenge-${ch.id}`}>
                <div className="flex items-center justify-between mb-2"><p className="font-heading text-sm font-semibold text-slate-700">{ch.name}</p><Badge className="bg-amber-50 text-amber-600 text-xs">+{ch.xp} Veda Coins</Badge></div>
                <p className="text-xs text-slate-500 font-body mb-2">{ch.desc}</p>
                <div className="flex items-center gap-2"><Progress value={(ch.current / ch.target) * 100} className="h-1.5 flex-1" /><span className="text-xs text-slate-400 font-body">{ch.current}/{ch.target}</span></div>
              </Card>
            ))}
          </div>
        </Card>
      )}

      {/* Badges Section */}
      {g.badges && (
        <Card className="p-5 rounded-2xl border-slate-100 mb-6 animate-slide-up" data-testid="badges-section">
          <div className="flex items-center gap-2 mb-4"><Award className="w-5 h-5 text-purple-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Badges</h3><Badge className="bg-slate-100 text-slate-600 text-xs ml-1">{g.earned_badges}/{g.total_badges}</Badge></div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {g.badges.map(badge => {
              const IconComp = BADGE_ICONS[badge.icon] || Star;
              return (
                <div key={badge.id} className={`p-3 rounded-xl text-center transition-all ${badge.earned ? 'bg-blue-50 border-2 border-blue-200' : 'bg-slate-50 border border-slate-100 opacity-50'}`} data-testid={`badge-${badge.id}`}>
                  <IconComp className={`w-6 h-6 mx-auto mb-1.5 ${badge.earned ? 'text-blue-500' : 'text-slate-300'}`} /><p className={`font-heading text-xs font-semibold ${badge.earned ? 'text-slate-800' : 'text-slate-400'}`}>{badge.name}</p><p className="text-[10px] text-slate-400 font-body mt-0.5">{badge.desc}</p>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Tabbed Charts Section */}
      <Tabs defaultValue="progress" className="space-y-4">
        <TabsList className="bg-white border border-slate-200 rounded-2xl p-1" data-testid="dashboard-tabs">
          <TabsTrigger value="progress" className="rounded-xl font-heading text-xs"><TrendingUp className="w-3.5 h-3.5 mr-1" />Progress</TabsTrigger>
          <TabsTrigger value="activity" className="rounded-xl font-heading text-xs"><Calendar className="w-3.5 h-3.5 mr-1" />Activity</TabsTrigger>
          <TabsTrigger value="subjects" className="rounded-xl font-heading text-xs"><BookOpen className="w-3.5 h-3.5 mr-1" />Subjects</TabsTrigger>
          <TabsTrigger value="leaderboard" className="rounded-xl font-heading text-xs"><Trophy className="w-3.5 h-3.5 mr-1" />Leaderboard</TabsTrigger>
        </TabsList>

        {/* Progress Tab: Score Trend + Recent Quizzes */}
        <TabsContent value="progress">
          <Card className="p-5 rounded-2xl border-slate-100 mb-4">
            <h3 className="font-heading text-base font-semibold text-slate-800 mb-1">Score Trend</h3>
            <p className="text-xs text-slate-400 font-body mb-4">Quiz scores over time with running average</p>
            <ScoreTrendChart data={g.score_trend} />
          </Card>
          <Card className="p-5 rounded-2xl border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-base font-semibold text-slate-800">Recent Quizzes</h3>
              {(g.best_score || 0) > 0 && <Badge className="bg-amber-50 text-amber-600 text-xs"><Trophy className="w-3 h-3 mr-1" />Best: {g.best_score}%</Badge>}
            </div>
            {!g.recent_quizzes?.length ? (
              <p className="text-sm text-slate-400 font-body text-center py-6">No quizzes taken yet</p>
            ) : (
              <div className="space-y-2">{g.recent_quizzes.map((q, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`recent-quiz-${i}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading text-sm font-bold ${q.percentage >= 80 ? 'bg-emerald-100 text-emerald-600' : q.percentage >= 50 ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600'}`}>{q.percentage}%</div>
                  <div className="flex-1 min-w-0"><p className="font-heading text-sm font-medium text-slate-700 truncate">{q.subject}</p><p className="text-xs text-slate-400 font-body">{q.score}/{q.total} correct &middot; {q.topic}</p></div>
                  <span className="text-xs text-slate-400 font-body hidden sm:inline">{q.created_at?.slice(0, 10)}</span>
                </div>
              ))}</div>
            )}
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card className="p-5 rounded-2xl border-slate-100">
            <h3 className="font-heading text-base font-semibold text-slate-800 mb-1">Daily Activity</h3>
            <p className="text-xs text-slate-400 font-body mb-4">Quizzes per day (last 14 days)</p>
            <ActivityChart data={g.daily_activity} />
          </Card>
        </TabsContent>

        {/* Subjects Tab */}
        <TabsContent value="subjects">
          <SubjectsTab subjectAnalytics={subjectAnalytics?.subjects} />
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard">
          <Card className="p-5 rounded-2xl border-slate-100">
            <h3 className="font-heading text-base font-semibold text-slate-800 mb-4">Leaderboard</h3>
            {!leaderboard?.enabled ? <p className="text-sm text-slate-400 font-body text-center py-6">Leaderboard is currently disabled</p> : (
              <div className="space-y-2">
                {leaderboard.user_rank > 0 && (
                  <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 mb-3 flex items-center justify-between"><span className="text-sm font-body text-blue-700">Your Rank</span><Badge className="bg-blue-100 text-blue-700 font-heading text-base px-3">#{leaderboard.user_rank}</Badge></div>
                )}
                {leaderboard.entries?.slice(0, 10).map((entry, i) => (
                  <div key={entry.user_id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-heading font-bold ${i === 0 ? 'bg-amber-100 text-amber-600' : i === 1 ? 'bg-slate-200 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                    <p className="text-sm font-body text-slate-700 flex-1 truncate">{entry.name}</p>
                    <span className="text-sm font-heading font-semibold text-slate-600">{entry.total_points} Veda Coins</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
      {showShareStreak && <ShareableStreakCard user={user} streakDays={g?.streak || 0} onClose={() => setShowShareStreak(false)} />}
    </div>
  );
}
